import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';

import { ApiUrlService } from './api-url.service';
import {
  CrearNuevaSolicitud,
  SolicitudDetalleResponse,
  SolicitudesListadoItem,
  SolicitudesListadoResponse
} from '../models/solicitudes';
import { normalizarTextoMayusculasSinTildes } from '../utils/text-normalization';

@Injectable({
  providedIn: 'root'
})
export class ClienteRequestsService {
  private readonly basePath = '/api/v1/cliente-farmacia/requests';
  private readonly pendientesRefreshSubject = new Subject<SolicitudesListadoItem[] | null>();
  readonly pendientesRefresh$ = this.pendientesRefreshSubject.asObservable();

  constructor(private apiUrl: ApiUrlService, private http: HttpClient) {}

  private resolveIdUsuario(source: Record<string, any>): string | null | undefined {
    return source?.['id_usuario']
      ?? source?.['idusuario']
      ?? source?.['idUsuario']
      ?? source?.['solicitud']?.['id_usuario']
      ?? source?.['solicitud']?.['idusuario']
      ?? source?.['solicitud']?.['idUsuario'];
  }

  private normalizeUpdatePayload(idSolicitud: number, solicitud: Record<string, any>): Record<string, any> {
    return {
      ...solicitud,
      id_solicitud: solicitud?.['id_solicitud'] ?? solicitud?.['id'] ?? idSolicitud,
      id_usuario: this.resolveIdUsuario(solicitud),
      fecha_creacion: solicitud?.['fecha_creacion'] ?? solicitud?.['createdAt'] ?? solicitud?.['created_at'],
      fecha_actualizacion: solicitud?.['fecha_actualizacion'] ?? solicitud?.['updatedAt'] ?? solicitud?.['updated_at'],
      prescriptor: solicitud?.['prescriptor'] == null
        ? null
        : normalizarTextoMayusculasSinTildes(solicitud['prescriptor']),
      nombre_paciente: (solicitud?.['nombre_paciente'] ?? solicitud?.['nombrePaciente']) == null
        ? null
        : normalizarTextoMayusculasSinTildes(solicitud?.['nombre_paciente'] ?? solicitud?.['nombrePaciente'])
    };
  }

  public notificarPendientesRefresh(items?: SolicitudesListadoItem[]): void {
    this.pendientesRefreshSubject.next(items ?? null);
  }

  public obtenerListadoSolicitudes(): Observable<SolicitudesListadoResponse> {
    return this.http.get<SolicitudesListadoResponse>(this.apiUrl.build(this.basePath));
  }

  public obtenerDetalleSolicitud(idSolicitud: number): Observable<SolicitudDetalleResponse> {
    return this.http.get<SolicitudDetalleResponse>(this.apiUrl.build(`${this.basePath}/${idSolicitud}`));
  }

  public crearSolicitud(payload: CrearNuevaSolicitud | FormData): Observable<SolicitudDetalleResponse> {
    const payloadNormalizado = payload instanceof FormData
      ? payload
      : {
        ...payload,
        prescriptor: normalizarTextoMayusculasSinTildes(payload.prescriptor),
        nombrePaciente: normalizarTextoMayusculasSinTildes(payload.nombrePaciente)
      };
    return this.http.post<SolicitudDetalleResponse>(this.apiUrl.build(this.basePath), payloadNormalizado);
  }

  public actualizarDetalleSolicitud(idSolicitud: number, solicitud: Record<string, any>): Observable<SolicitudDetalleResponse> {
    const payload = this.normalizeUpdatePayload(idSolicitud, solicitud);
    return this.http.put<SolicitudDetalleResponse>(this.apiUrl.build(`${this.basePath}/${idSolicitud}`), payload);
  }

  public actualizarEstadoSolicitud(
    idSolicitud: number,
    estado: string,
    comentario?: string,
    idUsuario?: string | null
  ): Observable<SolicitudDetalleResponse> {
    void idUsuario;
    if (estado.toUpperCase() !== 'CANCELADA') {
      return throwError(() => new Error('El portal cliente solo puede cancelar solicitudes.'));
    }
    const payload: Record<string, any> = {};
    const comentarioNormalizado = comentario?.trim();
    if (comentarioNormalizado) payload['comentario'] = comentarioNormalizado;
    return this.http.put<SolicitudDetalleResponse>(
      this.apiUrl.build(`${this.basePath}/${idSolicitud}/cancel`),
      payload
    );
  }

  public obtenerHistorialSolicitud(idSolicitud: number): Observable<any> {
    return this.http.get<any>(this.apiUrl.build(`${this.basePath}/${idSolicitud}/history`));
  }

  public obtenerImagenSolicitud(idSolicitud: number | string, cacheBuster?: number): Observable<Blob> {
    const url = this.apiUrl.build(`${this.basePath}/${idSolicitud}/attachment`);
    const options = cacheBuster
      ? { params: { t: String(cacheBuster) }, responseType: 'blob' as const }
      : { responseType: 'blob' as const };
    return this.http.get(url, options);
  }

  public subirImagenSolicitud(idSolicitud: number | string, imagen: File): Observable<{ message?: string }> {
    const payload = new FormData();
    payload.append('imagen', imagen, imagen.name);
    return this.http.post<{ message?: string }>(
      this.apiUrl.build(`${this.basePath}/${idSolicitud}/attachment`),
      payload
    );
  }

  public eliminarImagenSolicitud(idSolicitud: number | string): Observable<{ message?: string }> {
    return this.http.delete<{ message?: string }>(this.apiUrl.build(`${this.basePath}/${idSolicitud}/attachment`));
  }
}
